import { debounce } from "lodash";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  BsCameraVideo,
  BsFileEarmarkCode,
  BsFileEarmarkExcel,
  BsFileEarmarkPdf,
  BsFileEarmarkPpt,
  BsFileEarmarkText,
  BsFileEarmarkWord,
  BsFileEarmarkZip,
  BsFiletypeCsv,
  BsFiletypeHtml,
  BsFiletypeJs,
  BsImage,
  BsMusicNoteBeamed,
  BsQuestionCircle,
} from "react-icons/bs";
import apiFactory from "../api";
import { STORAGE } from "../config/Constant";

const DEFAULT_MEDIA_TYPES = [
  {
    value: "image/",
    title: "All images",
    label: (
      <div className="flex items-center gap-2">
        <BsImage className="text-[#3b82f6] text-[15px]" />
        <span>All images</span>
      </div>
    ),
  },
  {
    value: "video/",
    title: "All videos",
    label: (
      <div className="flex items-center gap-2">
        <BsCameraVideo className="text-[#ef4444] text-[15px]" />
        <span>All videos</span>
      </div>
    ),
  },
  {
    value: "audio/",
    title: "All audios",
    label: (
      <div className="flex items-center gap-2">
        <BsMusicNoteBeamed className="text-[#8b5cf6] text-[15px]" />
        <span>All audios</span>
      </div>
    ),
  },
  {
    value: "application/pdf",
    title: "PDF",
    label: (
      <div className="flex items-center gap-2">
        <BsFileEarmarkPdf className="text-[#dc2626] text-[15px]" />
        <span>PDF</span>
      </div>
    ),
  },
  {
    value: "application/msword",
    title: "Word (DOC/DOCX)",
    label: (
      <div className="flex items-center gap-2">
        <BsFileEarmarkWord className="text-[#2563eb] text-[15px]" />
        <span>Word (DOC/DOCX)</span>
      </div>
    ),
  },
  {
    value: "application/vnd.ms-excel",
    title: "Excel (XLS/XLSX)",
    label: (
      <div className="flex items-center gap-2">
        <BsFileEarmarkExcel className="text-[#16a34a] text-[15px]" />
        <span>Excel (XLS/XLSX)</span>
      </div>
    ),
  },
  {
    value: "text/csv",
    title: "CSV",
    label: (
      <div className="flex items-center gap-2">
        <BsFiletypeCsv className="text-[#0d9488] text-[15px]" />
        <span>CSV</span>
      </div>
    ),
  },
  {
    value: "application/vnd.ms-powerpoint",
    title: "PowerPoint",
    label: (
      <div className="flex items-center gap-2">
        <BsFileEarmarkPpt className="text-[#ea580c] text-[15px]" />
        <span>PowerPoint</span>
      </div>
    ),
  },
  {
    value: "application/zip",
    title: "ZIP / RAR",
    label: (
      <div className="flex items-center gap-2">
        <BsFileEarmarkZip className="text-[#ca8a04] text-[15px]" />
        <span>ZIP / RAR</span>
      </div>
    ),
  },
  {
    value: "text/plain",
    title: "Text (.txt)",
    label: (
      <div className="flex items-center gap-2">
        <BsFileEarmarkText className="text-[#64748b] text-[15px]" />
        <span>Text (.txt)</span>
      </div>
    ),
  },
  {
    value: "application/json",
    title: "JSON",
    label: (
      <div className="flex items-center gap-2">
        <BsFileEarmarkCode className="text-[#6366f1] text-[15px]" />
        <span>JSON</span>
      </div>
    ),
  },
  {
    value: "text/html",
    title: "HTML",
    label: (
      <div className="flex items-center gap-2">
        <BsFiletypeHtml className="text-[#f97316] text-[15px]" />
        <span>HTML</span>
      </div>
    ),
  },
  {
    value: "application/javascript",
    title: "JavaScript",
    label: (
      <div className="flex items-center gap-2">
        <BsFiletypeJs className="text-[#eab308] text-[15px]" />
        <span>JavaScript</span>
      </div>
    ),
  },
  {
    value: "unknown",
    title: "Unknown",
    label: (
      <div className="flex items-center gap-2">
        <BsQuestionCircle className="text-[#94a3b8] text-[15px]" />
        <span>Unknown</span>
      </div>
    ),
  },
];

const StorageContext = createContext(null);
export const useStorageContext = () => {
  return useContext(StorageContext);
};

export const StorageProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [storageSearch, setStorageSearch] = useState({
    fileName: "",
    name: "",
    mediaType: null,
    conversationType: null,
    dateType: null,
    selectedDate: null,
    fromDate: null,
    toDate: null,
  });

  const [mediaTypes, setMediaType] = useState(DEFAULT_MEDIA_TYPES);

  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
  });

  const [resourceList, setResourceList] = useState([]);

  const hasMore = useMemo(() => {
    return pagination?.page * STORAGE.STORAGE_LIMIT < pagination?.total;
  }, [pagination?.page, pagination?.total]);

  const getCleanSearchParams = (searchObj) => {
    const formatDayjs = (val) => {
      if (!val) return null;
      if (typeof val?.format === "function") return val.format("YYYY-MM-DD");
      if (typeof val === "string" && val.length >= 10) return val.substring(0, 10);
      return val;
    };

    return {
      ...searchObj,
      selectedDate: formatDayjs(searchObj?.selectedDate),
      fromDate: formatDayjs(searchObj?.fromDate),
      toDate: formatDayjs(searchObj?.toDate),
    };
  };

  const fetchResourceList = async () => {
    setIsLoading(true);
    try {
      const result = await apiFactory.resourceApi.getStorage({
        search: getCleanSearchParams(storageSearch),
        page: pagination?.page,
        limit: STORAGE.STORAGE_LIMIT,
      });
      if (result?.status === 200) {
        setResourceList(result?.data?.items || []);
        setPagination({ ...pagination, total: result?.data?.totalItems || 0 });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const initResourceList = async () => {
    setIsLoading(true);
    try {
      const result = await apiFactory.resourceApi.getStorage({
        search: getCleanSearchParams(storageSearch),
        page: 1,
        limit: STORAGE.STORAGE_LIMIT,
      });
      if (result?.status === 200) {
        setResourceList(result?.data?.items || []);
        setPagination({ page: 1, total: result?.data?.totalItems || 0 });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const viewMore = async () => {
    if (isLoading || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const nextPage = pagination?.page + 1;
      const result = await apiFactory.resourceApi.getStorage({
        search: getCleanSearchParams(storageSearch),
        page: nextPage,
        limit: STORAGE.STORAGE_LIMIT,
      });
      if (result?.status === 200) {
        setResourceList((prev) => [...prev, ...(result?.data?.items || [])]);
        setPagination((prev) => ({
          ...prev,
          page: nextPage,
          total: result?.data?.totalItems || prev.total,
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const searchFileName = debounce((fileName) => {
    resetPagination();
    setStorageSearch((prev) => ({
      ...prev,
      name: fileName,
    }));
  }, 500);

  const onChangeSearch = (e) => {
    const fileName = e.target.value.trim();
    setStorageSearch((prev) => ({
      ...prev,
      fileName: fileName,
    }));
    searchFileName(fileName);
  };

  const resetPagination = () => {
    setPagination({
      page: 1,
      total: 0,
    });
  };

  const onChangeDateType = (value) => {
    const isNotDateTypeChanged =
      (value === "DATE" &&
        !storageSearch?.fromDate &&
        !storageSearch?.toDate) ||
      (value === "RANGE" && !storageSearch?.selectedDate) ||
      (!value &&
        !storageSearch?.selectedDate &&
        !storageSearch?.fromDate &&
        !storageSearch?.toDate);

    if (!isNotDateTypeChanged) {
      resetPagination();
    }

    setStorageSearch((prev) => ({
      ...prev,
      dateType: value,
      selectedDate: null,
      fromDate: null,
      toDate: null,
    }));
  };

  useEffect(() => {
    initResourceList();
  }, [
    storageSearch?.conversationType,
    storageSearch?.name,
    storageSearch?.selectedDate,
    storageSearch?.fromDate,
    storageSearch?.toDate,
    storageSearch?.mediaType,
  ]);

  const values = useMemo(
    () => ({
      isLoading,
      isLoadingMore,
      hasMore,
      storageSearch,
      setStorageSearch,
      setPagination,
      resetPagination,
      resourceList,
      setResourceList,
      pagination,
      viewMore,
      onChangeSearch,
      onChangeDateType,
      initResourceList,
      mediaTypes,
    }),
    [storageSearch, resourceList, pagination, isLoading, isLoadingMore, hasMore, mediaTypes]
  );

  return (
    <StorageContext.Provider value={values}>{children}</StorageContext.Provider>
  );
};
